import { useEffect, useRef, useState, type ChangeEvent, type ClipboardEvent } from "react";
import { cn } from "./ui";
import useFeedback, { addFeedback, feedbackKindMeta, requestFeedbackJournal, type FeedbackKind } from "./feedback-store";
import { useAuth } from "./auth";
import { apiErrorText } from "@/lib/api";
import { t } from "@/lib/i18n";
import { domToCanvas } from "modern-screenshot";

/* Плашка «Идея / Проблема»: пользователь пишет замечание, и к нему автоматически снимается текущий экран
   (раздел, который сейчас на виду, без системного диалога), запись попадает в журнал замечаний, откуда её
   забирает робот (Claude Code) вместе со снимком. Запасные пути — снимок окна браузера, файл или ⌘V. */

const MAX_SHOT_WIDTH = 1024;

type ScreenInfo = { label: string; anchor: string };

/** Раздел, который сейчас на экране: ближайший section[id] выше верхней кромки окна */
function currentSection(): HTMLElement | null {
  const sections = Array.from(document.querySelectorAll<HTMLElement>("section[id]"));
  let best: HTMLElement | null = null;
  for (let i = 0; i < sections.length; i += 1) {
    if (sections[i].getBoundingClientRect().top <= 140) best = sections[i];
  }
  return best ?? sections[0] ?? null;
}

/** Текущий экран: раздел и его заголовок */
function detectScreen(): ScreenInfo {
  const el = currentSection();
  if (!el) return { label: t("Страница Hub"), anchor: "" };
  const heading = el.querySelector("h1, h2, h3");
  const title = (heading?.textContent || "").replace(/\s+/g, " ").trim();
  return { label: title || `#${el.id}`, anchor: `#${el.id}` };
}

const viewportLabel = () => `${window.innerWidth} × ${window.innerHeight}`;

/** Уменьшаем картинку в браузере, чтобы скриншот не раздувал запись */
function shrink(source: HTMLVideoElement | HTMLImageElement, width: number, height: number): string | null {
  const scale = Math.min(1, MAX_SHOT_WIDTH / Math.max(width, 1));
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(width * scale));
  canvas.height = Math.max(1, Math.round(height * scale));
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;
  ctx.drawImage(source, 0, 0, canvas.width, canvas.height);
  return canvas.toDataURL("image/jpeg", 0.72);
}

/** Автоматический снимок: текущий раздел рисуется из DOM (modern-screenshot), без системного диалога.
    Берём видимую часть раздела — то, что человек видит в окне, но не меньше одного экрана. */
async function captureDom(): Promise<string | null> {
  const el = currentSection() ?? document.body;
  const rect = el.getBoundingClientRect();
  if (rect.width < 10 || rect.height < 10) return null;
  const scale = Math.min(1, MAX_SHOT_WIDTH / rect.width);
  const canvas = await domToCanvas(el, {
    scale,
    quality: 0.72,
    timeout: 8000,
    filter: (node) => !(node instanceof HTMLElement && node.hasAttribute("data-feedback-root")),
    font: { preferredFormat: "woff2" },
  });
  const top = Math.max(0, -rect.top);
  const height = Math.min(rect.height - top, Math.max(window.innerHeight, window.innerHeight - rect.top));
  const out = document.createElement("canvas");
  out.width = canvas.width;
  out.height = Math.max(1, Math.round(height * scale));
  const ctx = out.getContext("2d");
  if (!ctx) return null;
  ctx.fillStyle = "#fff";
  ctx.fillRect(0, 0, out.width, out.height);
  ctx.drawImage(canvas, 0, Math.round(top * scale), out.width, out.height, 0, 0, out.width, out.height);
  return out.toDataURL("image/jpeg", 0.72);
}

/** Снимок экрана браузера: пользователь сам выбирает вкладку в системном окне */
async function captureScreen(): Promise<string | null> {
  const media = navigator.mediaDevices;
  if (!media || typeof media.getDisplayMedia !== "function") return null;
  const stream = await media.getDisplayMedia({ video: true, audio: false });
  try {
    const video = document.createElement("video");
    video.muted = true;
    video.srcObject = stream;
    await new Promise<void>((resolve) => {
      video.onloadedmetadata = () => resolve();
    });
    await video.play().catch(() => undefined);
    await new Promise((resolve) => window.setTimeout(resolve, 220));
    return shrink(video, video.videoWidth || 1280, video.videoHeight || 720);
  } finally {
    stream.getTracks().forEach((track) => track.stop());
  }
}

function fileToShot(file: File): Promise<string | null> {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => resolve(shrink(img, img.naturalWidth, img.naturalHeight));
      img.onerror = () => resolve(null);
      img.src = String(reader.result);
    };
    reader.onerror = () => resolve(null);
    reader.readAsDataURL(file);
  });
}

export default function FeedbackWidget() {
  const { user } = useAuth();
  const { items } = useFeedback();
  // администратору — новые замечания, автору — исправленные и ждущие его проверки
  const newCount = items.filter((i) => (user?.can.manageFeedback ? i.status === "new" : i.status === "testing" && i.user_id === user?.id)).length;
  const [open, setOpen] = useState(false);
  const [kind, setKind] = useState<FeedbackKind>("problem");
  const [text, setText] = useState("");
  const [shot, setShot] = useState<string | undefined>();
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [screen, setScreen] = useState<ScreenInfo>({ label: t("Страница Hub"), anchor: "" });
  const rootRef = useRef<HTMLDivElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  // при открытии фиксируем экран, с которого пишут замечание, и сразу снимаем его
  useEffect(() => {
    if (!open) return;
    setScreen(detectScreen());
    setSent(false);
    let alive = true;
    setBusy(true);
    captureDom()
      .then((data) => {
        if (alive && data) setShot((prev) => prev ?? data);
      })
      .catch(() => undefined)
      .finally(() => {
        if (alive) setBusy(false);
      });
    return () => {
      alive = false;
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    const onDown = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null;
      if (target?.closest("[data-feedback-root]")) return;
      setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onDown);
    return () => {
      window.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onDown);
    };
  }, [open]);

  const reset = () => {
    setText("");
    setShot(undefined);
    setNotice("");
    setSent(false);
  };

  const takeShot = async () => {
    setBusy(true);
    setNotice("");
    try {
      // сначала снимок раздела из DOM, если не вышло — системный снимок окна браузера
      const data = (await captureDom().catch(() => null)) ?? (await captureScreen());
      if (data) {
        setShot(data);
        setNotice("");
      } else {
        setNotice(t("Браузер не поддерживает съёмку экрана — прикрепите файл или вставьте картинку из буфера."));
      }
    } catch (err) {
      const name = err instanceof Error ? err.name : "";
      setNotice(
        name === "NotAllowedError"
          ? t("Съёмка экрана отменена. Можно прикрепить файл или вставить картинку из буфера.")
          : t("Не удалось снять экран. Прикрепите файл — замечание всё равно сохранится."),
      );
    } finally {
      setBusy(false);
    }
  };

  const pickFile = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setNotice(t("Нужна картинка: PNG, JPG или скриншот."));
      return;
    }
    setBusy(true);
    const data = await fileToShot(file);
    setBusy(false);
    if (data) {
      setShot(data);
      setNotice("");
    } else {
      setNotice(t("Не удалось прочитать файл. Попробуйте другой скриншот."));
    }
  };

  const pasteShot = async (e: ClipboardEvent<HTMLTextAreaElement>) => {
    const file = Array.from(e.clipboardData.items).find((i) => i.type.startsWith("image/"));
    if (!file) return;
    e.preventDefault();
    const blob = file.getAsFile();
    if (!blob) return;
    setBusy(true);
    const data = await fileToShot(blob);
    setBusy(false);
    if (data) {
      setShot(data);
      setNotice("");
    }
  };

  const submit = async () => {
    if (text.trim().length < 5) {
      setNotice(t("Опишите замечание хотя бы одним предложением — так роботу будет понятнее."));
      return;
    }
    setBusy(true);
    try {
      await addFeedback({
        kind,
        text,
        screen: screen.label,
        anchor: screen.anchor,
        viewport: viewportLabel(),
        screenshot: shot,
      });
      setSent(true);
      setNotice("");
    } catch (err) {
      setNotice(apiErrorText(err));
    } finally {
      setBusy(false);
    }
  };

  const openJournal = () => {
    setOpen(false);
    requestFeedbackJournal();
  };

  return (
    <div ref={rootRef} data-feedback-root>
      {/* сама плашка */}
      <div className="fixed bottom-24 end-4 z-[65] flex flex-col items-end gap-2 sm:bottom-5 sm:end-5">
        {open ? (
          <div
            role="dialog"
            aria-label={t("Обратная связь: идея или проблема")}
            className="w-[min(24rem,calc(100vw-2rem))] overflow-hidden rounded-card border border-border bg-card text-card-foreground shadow-lift"
          >
            <div className="flex items-start gap-2.5 border-b border-border px-3.5 py-2.5">
              <span className="mt-0.5 grid size-7 flex-none place-items-center rounded-btn bg-primary/12 text-primary" aria-hidden>
                <i className="ri-feedback-line text-[15px] leading-none" />
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-[12.5px] font-semibold leading-tight">{t("Обратная связь")}</p>
                <p className="mt-0.5 text-[10.5px] leading-relaxed text-muted-foreground">
                  {t("Замечание уйдёт в журнал, администратор передаст его роботу на исправление.")}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                aria-label={t("Закрыть форму обратной связи")}
                className="grid size-6 flex-none cursor-pointer place-items-center rounded-btn text-muted-foreground transition hover:bg-muted hover:text-foreground"
              >
                <i className="ri-close-line text-sm leading-none" aria-hidden />
              </button>
            </div>

            {sent ? (
              <div className="flex flex-col items-center gap-2 px-4 py-6 text-center">
                <span className="grid size-11 place-items-center rounded-full bg-primary/12 text-primary" aria-hidden>
                  <i className="ri-checkbox-circle-line text-xl leading-none" />
                </span>
                <p className="text-[13px] font-semibold">{t("Замечание в журнале")}</p>
                <p className="max-w-[17rem] text-[11px] leading-relaxed text-muted-foreground">
                  {shot ? `${t("Скриншот приложен.")} ` : ""}
                  {t("Администратор посмотрит его и передаст роботу. Когда будет исправлено, статус в журнале сменится на «Исправлено — проверьте».")}
                </p>
                <div className="mt-1.5 flex flex-wrap items-center justify-center gap-1.5">
                  <button
                    type="button"
                    onClick={openJournal}
                    className="inline-flex h-8 cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-btn bg-primary px-3 text-[11.5px] font-semibold text-primary-foreground transition hover:brightness-110"
                  >
                    <i className="ri-list-check-2 text-[13px] leading-none" aria-hidden />
                    {t("Открыть журнал")}
                  </button>
                  <button
                    type="button"
                    onClick={reset}
                    className="inline-flex h-8 cursor-pointer items-center whitespace-nowrap rounded-btn border border-border px-2.5 text-[11.5px] font-semibold transition hover:border-primary/50 hover:text-primary"
                  >
                    {t("Ещё одно")}
                  </button>
                </div>
              </div>
            ) : (
              <>
                <div className="flex flex-col gap-2.5 p-3.5">
                  <div className="flex gap-1 rounded-full bg-foreground/[.05] px-1 py-1" role="group" aria-label={t("Тип замечания")}>
                    {(Object.keys(feedbackKindMeta) as FeedbackKind[]).map((k) => {
                      const meta = feedbackKindMeta[k];
                      const active = kind === k;
                      return (
                        <button
                          key={k}
                          type="button"
                          onClick={() => setKind(k)}
                          aria-pressed={active}
                          className={cn(
                            "inline-flex h-7 flex-1 cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-full px-2 text-[11.5px] font-semibold transition",
                            active ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground",
                          )}
                        >
                          <i className={cn(meta.icon, "text-[13px] leading-none", active && (k === "problem" ? "text-red-500" : "text-primary"))} aria-hidden />
                          {meta.label}
                        </button>
                      );
                    })}
                  </div>

                  <div>
                    <textarea
                      value={text}
                      onChange={(e) => {
                        setText(e.target.value);
                        if (notice) setNotice("");
                      }}
                      onPaste={pasteShot}
                      rows={4}
                      maxLength={500}
                      placeholder={
                        kind === "problem"
                          ? t("Что пошло не так? На каком экране, что нажали и что ожидали увидеть.")
                          : t("Чего не хватает и как это поможет в работе? Опишите сценарий.")
                      }
                      className="h-auto min-h-[5.2rem] w-full box-border resize-none rounded-btn border border-border bg-background px-2.5 py-2 text-[12px] leading-relaxed text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                    />
                    <div className="mt-1 flex items-center justify-between gap-2 text-[10px] text-muted-foreground">
                      <span className="inline-flex items-center gap-1">
                        <i className="ri-attachment-line text-[12px] leading-none" aria-hidden />
                        {t("Скриншот прикладывается к записи")}
                      </span>
                      <span className="tabular-nums">{text.length}/500</span>
                    </div>
                  </div>

                  {shot ? (
                    <div className="overflow-hidden rounded-btn border border-border bg-background">
                      <div className="relative h-24 w-full">
                        <img src={shot} alt={t("Скриншот экрана, с которого отправляют замечание")} className="absolute inset-0 size-full object-cover object-top" />
                        <button
                          type="button"
                          onClick={() => setShot(undefined)}
                          aria-label={t("Убрать скриншот")}
                          className="absolute end-1.5 top-1.5 grid size-6 cursor-pointer place-items-center rounded-full bg-black/60 text-white transition hover:bg-black/80"
                        >
                          <i className="ri-delete-bin-line text-[12px] leading-none" aria-hidden />
                        </button>
                      </div>
                      <p className="flex items-center gap-1.5 border-t border-border px-2 py-1.5 text-[10px] text-muted-foreground">
                        <i className="ri-image-line text-[12px] leading-none text-primary" aria-hidden />
                        {t("Скриншот экрана приложен — робот увидит этот экран. Не тот? Уберите и снимите заново или приложите файл.")}
                      </p>
                    </div>
                  ) : (
                    <div className="rounded-btn border border-dashed border-border bg-background/50 p-2.5">
                      <div className="flex items-center gap-2">
                        <span className="grid size-8 flex-none place-items-center rounded-btn bg-foreground/[.05] text-foreground/60" aria-hidden>
                          <i className="ri-screenshot-2-line text-[15px] leading-none" />
                        </span>
                        <div className="min-w-0 flex-1">
                          <p className="text-[11.5px] font-semibold leading-tight">{t("Скриншот экрана")}</p>
                          <p className="mt-0.5 text-[10px] leading-relaxed text-muted-foreground">
                            {busy ? t("Снимаем текущий экран…") : t("Снимем текущий экран или приложим файл, можно вставить ⌘V.")}
                          </p>
                        </div>
                      </div>
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        <button
                          type="button"
                          onClick={takeShot}
                          disabled={busy}
                          className="inline-flex h-7 cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-btn bg-primary px-2.5 text-[11px] font-semibold text-primary-foreground transition hover:brightness-110 disabled:opacity-60"
                        >
                          <i className={cn(busy ? "ri-loader-4-line animate-spin" : "ri-camera-line", "text-[12px] leading-none")} aria-hidden />
                          {busy ? t("Снимаю…") : t("Снять экран")}
                        </button>
                        <button
                          type="button"
                          onClick={() => fileRef.current?.click()}
                          className="inline-flex h-7 cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-btn border border-border px-2.5 text-[11px] font-semibold transition hover:border-primary/50 hover:text-primary"
                        >
                          <i className="ri-upload-2-line text-[12px] leading-none" aria-hidden />
                          {t("Прикрепить файл")}
                        </button>
                        <input ref={fileRef} type="file" accept="image/*" onChange={pickFile} className="hidden" aria-hidden tabIndex={-1} />
                      </div>
                    </div>
                  )}

                  <div className="flex flex-wrap items-center gap-1.5 text-[10px] text-muted-foreground">
                    <span className="inline-flex min-w-0 items-center gap-1 rounded-full bg-foreground/[.04] px-2 py-0.5">
                      <i className="ri-window-line flex-none text-[11px] leading-none" aria-hidden />
                      <span className="truncate">{screen.label}</span>
                    </span>
                    <span className="rounded-full bg-foreground/[.04] px-2 py-0.5 tabular-nums">{viewportLabel()}</span>
                  </div>

                  {notice ? (
                    <p role="status" className="flex items-start gap-1.5 rounded-btn bg-red-500/10 px-2 py-1.5 text-[10.5px] leading-relaxed text-red-600">
                      <i className="ri-error-warning-line mt-[1px] flex-none text-[12px] leading-none" aria-hidden />
                      {notice}
                    </p>
                  ) : null}
                </div>

                <div className="flex items-center gap-2 border-t border-border bg-background/50 px-3.5 py-2">
                  <button
                    type="button"
                    onClick={() => void submit()}
                    disabled={busy}
                    className="inline-flex h-8 cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-btn bg-primary px-3 text-[11.5px] font-semibold text-primary-foreground transition hover:brightness-110 disabled:opacity-60"
                  >
                    <i className="ri-send-plane-line text-[13px] leading-none" aria-hidden />
                    {t("Отправить в журнал")}
                  </button>
                  <button
                    type="button"
                    onClick={openJournal}
                    className="ms-auto inline-flex cursor-pointer items-center gap-1 whitespace-nowrap text-[10.5px] font-semibold text-foreground/70 transition hover:text-primary"
                  >
                    {t("Журнал")}
                    {newCount > 0 ? <span className="rounded-full bg-primary/12 px-1.5 text-[9.5px] font-bold text-primary tabular-nums">{newCount}</span> : null}
                    <i className="ri-arrow-right-line text-[11px] leading-none" aria-hidden />
                  </button>
                </div>
              </>
            )}
          </div>
        ) : null}

        <button
          type="button"
          onClick={() => {
            setOpen((v) => !v);
            if (open) reset();
          }}
          aria-expanded={open}
          aria-label={open ? t("Свернуть форму обратной связи") : t("Идея или проблема: обратная связь")}
          className={cn(
            "relative inline-flex h-11 cursor-pointer items-center gap-2 whitespace-nowrap rounded-full border px-3 shadow-lift transition",
            open ? "border-primary/50 bg-primary text-primary-foreground hover:brightness-110" : "border-border bg-card text-foreground hover:border-primary/50",
          )}
        >
          <i className={cn(open ? "ri-close-line" : "ri-feedback-line", "text-[17px] leading-none")} aria-hidden />
          <span className="hidden text-[12px] font-semibold sm:inline">{t("Идея / проблема")}</span>
          {!open && newCount > 0 ? (
            <span className="absolute -end-1 -top-1 grid h-4 min-w-4 place-items-center rounded-full bg-primary px-1 text-[9px] font-bold leading-none text-primary-foreground ring-2 ring-card tabular-nums">
              {newCount > 9 ? "9+" : newCount}
            </span>
          ) : null}
        </button>
      </div>
    </div>
  );
}